import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from 'react';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { sanitizeInnerHtml, useTranslate } from 'nucleus-text';
import Row from '@cvent/carina/components/Row';
import Col from '@cvent/carina/components/Col';
import Button from '@cvent/carina/components/Button';
import TextLink from '@cvent/carina/components/TextLink';
import Spacer from './Spacer';
import useBreakpoints from './hooks/useBreakpoints';
import useCookieBannerStyles from './styles/cookieBannerStyles';
import useTheme from './hooks/useTheme';
import { useModalStatus, useOpenCookieModal, useSiteIdentifier } from './CookieConsentContext';
import { getConsentData } from './localStorageUtils';
import { ExternalLinkIcon } from '@cvent/carina/components/Icon';
const CookieBanner = ({ onAcceptAll, onAcceptEssential, testID, bannerType, hidePrivacyPolicyLink, customizedText = '' }) => {
    const { translate } = useTranslate();
    const { isS, isL, isXL, isMobileOrTablet, isMobile, isLandscapeMobile } = useBreakpoints();
    const { colors, font, transition } = useTheme();
    const siteIdentifier = useSiteIdentifier();
    const { isModalOpen } = useModalStatus();
    const openPreferencesModal = useOpenCookieModal();
    const backgroundColor = {
        active: colors === null || colors === void 0 ? void 0 : colors.neutral[10]
    };
    const styles = useCookieBannerStyles(backgroundColor, font, transition);
    const [hidden, setHidden] = useState(false);
    const onPressAll = () => {
        onAcceptAll();
        setHidden(true);
    };
    const onPressEssential = () => {
        onAcceptEssential();
        setHidden(true);
    };
    const [hideAnimationEnded, setHideAnimationEnded] = useState(false);
    const onAnimationEnd = () => {
        if (hidden) {
            setHideAnimationEnded(true);
        }
    };
    useEffect(() => {
        if (isModalOpen) {
            setHidden(true);
        }
        else if (!getConsentData(siteIdentifier)) {
            // if no consent data stored, user closed the modal without saving, so re-open the banner
            setHidden(false);
            setHideAnimationEnded(false);
        }
    }, [isModalOpen, siteIdentifier]);
    const essentialButtonEnabled = bannerType === 'essential' || bannerType === 'customize';
    const customButtonEnabled = bannerType === 'customize';
    const onlyAcceptButton = bannerType === 'accept';
    const buttonStyle = isMobile ? styles.mobileCookieBannerButton : styles.cookieBannerButton;
    const obtainCookieBannerBodyText = () => {
        if (customizedText === null || customizedText.trim() === '') {
            return onlyAcceptButton
                ? translate('CookieBanner-Text')
                : translate('CookieBanner-Customize-Text');
        }
        return customizedText;
    };
    const text = (_jsxs(_Fragment, { children: [_jsx("div", Object.assign({ css: styles.verticalScroll }, { children: _jsx("span", Object.assign({ css: isS ? styles.mobileText : styles.desktopText }, injectTestId(`${testID}-body`), { dangerouslySetInnerHTML: sanitizeInnerHtml(obtainCookieBannerBodyText()) })) })), _jsx(Spacer, { size: 1 / 4 }), !hidePrivacyPolicyLink && (_jsxs(TextLink, Object.assign({ href: "https://www.cvent.com/en/privacy-policy#cookies", target: "_blank", "aria-label": translate('CookieConsent-PrivacyPolicy-AccessibilityLabel'), rel: "noopener noreferrer", size: isS ? 'm' : 'l', testID: `${testID}-privacy-policy` }, { children: [translate('CookieBanner-PrivacyPolicy-Link') + ' ', _jsx(ExternalLinkIcon, {})] })))] }));
    const buttons = (_jsxs(Row, Object.assign({ justifyContent: isMobile || isLandscapeMobile ? 'center' : 'flex-end' }, { children: [essentialButtonEnabled && (_jsx(Button
            // eslint-disable-next-line jsx-a11y/tabindex-no-positive
            , { 
                // eslint-disable-next-line jsx-a11y/tabindex-no-positive
                tabIndex: 1, text: translate('CookieBanner-Essential-Accept-Button'), appearance: "filled", onClick: onPressEssential, testID: `${testID}-accept-essential`, size: isS ? 'm' : 'l', css: isLandscapeMobile ? styles.landscapeMobileCookieBannerButton : buttonStyle })), _jsx(Button, { text: translate(onlyAcceptButton
                    ? 'CookieBanner-Accept-Button'
                    : 'CookieBanner-Essential-Accept-All-Button'), tabIndex: onlyAcceptButton ? 0 : 2, appearance: "filled", onClick: onPressAll, testID: onlyAcceptButton ? `${testID}-accept` : `${testID}-accept-all`, size: isS ? 'm' : 'l', css: isLandscapeMobile ? styles.landscapeMobileCookieBannerButton : buttonStyle }), customButtonEnabled && (_jsx(Button, { text: translate('CookieBanner-Customize-Preferences-Button'), tabIndex: customButtonEnabled ? 3 : 0, appearance: "filled", onClick: openPreferencesModal, testID: `${testID}-customize-preferences`, size: isS ? 'm' : 'l', css: isLandscapeMobile ? styles.mobileCookieBannerButton : buttonStyle }))] })));
    return (_jsxs("div", Object.assign({ css: [
            styles.container,
            hidden ? styles.slideOut : styles.slideIn,
            hideAnimationEnded ? styles.hidden : null
        ], onAnimationEnd: onAnimationEnd, role: "form" }, injectTestId(testID), { children: [_jsx("h1", Object.assign({ hidden: true }, { children: translate('CookieBanner-AccessibilityHeading') })), _jsx(Spacer, { size: isMobileOrTablet ? 1 / 2 : 1 }), _jsxs(Row, { children: [isMobileOrTablet ? (_jsxs(_Fragment, { children: [_jsx(Col, { width: 0.5 / 12 }), _jsxs(Col, Object.assign({ width: "fill", padding: { top: 16, bottom: 16 } }, { children: [text, _jsx(Spacer, { size: 1 / 2 }), buttons] })), _jsx(Col, { width: 0.5 / 12 })] })) : null, isL ? (_jsxs(Row, { children: [_jsx(Col, { width: 0.5 / 12 }), _jsx(Col, { children: text }), _jsx(Col, { children: buttons }), _jsx(Col, { width: 0.5 / 12 })] })) : null, isXL ? (_jsxs(Row, { children: [_jsx(Col, { width: 0.5 / 12 }), _jsx(Col, { children: text }), _jsx(Col, { children: buttons }), _jsx(Col, { width: 0.5 / 12 })] })) : null] }), _jsx(Spacer, { size: isMobileOrTablet ? 1 / 2 : 1 })] })));
};
export default CookieBanner;
//# sourceMappingURL=CookieBannerComponent.js.map